/**
 * RemovedStepsSection
 *
 * The collapsible "Removed steps" block under an automation's Step Performance.
 * A removed step's sends still count in the page's totals, so its card stays
 * reachable here instead of the numbers simply not adding up. Each page renders
 * its own step cards inside; this owns the toggle and the removal wording.
 */
import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { formatPacificDateTime } from '@/lib/facilityTimezone';

/** The removal fields the stats endpoints add to a removed step's card. */
export interface StepRemoval {
  /** Where the step sat when it was removed. */
  previousStepOrder: number;
  /** Pacific wall-clock; null when the removal predates history. */
  removedAt: string | null;
  removedBy: string;
}

/** "Was step 2 · Removed Sep 24, 2026 3:15 PM PDT by Jane Doe". */
export function describeRemoval(removal: StepRemoval, tz: string): string {
  const was = `Was step ${removal.previousStepOrder}`;
  if (!removal.removedAt) return `${was} · Removed before removal dates were recorded`;
  const by = removal.removedBy ? ` by ${removal.removedBy}` : '';
  return `${was} · Removed ${formatPacificDateTime(removal.removedAt, tz)}${by}`;
}

interface RemovedStepsSectionProps {
  count: number;
  /** What the step numbers count, e.g. "emails" / "messages". */
  noun: string;
  children: ReactNode;
}

export function RemovedStepsSection({ count, noun, children }: RemovedStepsSectionProps) {
  const [open, setOpen] = useState(false);
  if (count === 0) return null;

  return (
    <div className="border-t border-[#e5e5e5]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-5 py-3 text-left text-sm font-semibold text-[#555] hover:bg-[#fafbfc]"
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        Removed steps ({count})
        <span className="font-normal text-xs text-[#999]">
          Steps taken out of this automation. The {noun} they sent still count in the totals above.
        </span>
      </button>
      {open && <div className="px-5 pb-4 pt-1">{children}</div>}
    </div>
  );
}
